<template>
  <div
    class="mb-2 flex justify-between text-base font-bold text-gray-darkest md:text-sm"
  >
    <span>
      <template v-if="activeOption">
        <div class="capitalize lg:inline">
          {{ store.kind?.name }}: {{ activeOption.title }}
        </div>
      </template>
      <template v-else>
        {{ t('product.options.text_option') }}&nbsp;{{ store.kind?.name }}
      </template>
    </span>
    <Transition name="fade">
      <span
        v-if="activeOption"
        class="flex items-center gap-1 text-base text-gray-800 before:inline-block before:size-3 before:rounded-full before:content-[''] lg:text-sm"
        :class="activeOption.inStock ? 'before:bg-green' : 'before:bg-pink'"
      >
        <template v-if="activeOption.inStock">
          {{ t('product.stock.text_instock_online') }}
        </template>
        <template v-else>
          {{ t('product.stock.text_oos_online') }}
        </template>
      </span>
    </Transition>
  </div>

  <div class="isolate mb-4 flex w-full justify-start gap-2">
    <div
      class="contents"
      role="radiogroup"
      :aria-label="t('product.options.color_picker')"
    >
      <GalleryOptionItem
        v-for="(option, index) in pickerOptions"
        :key="'option-' + option.id"
        :option="option"
        :is-active="activeOptionId == option.id"
        :is-focusable="!activeOptionId && index === 0"
        class="hb-swatch-tile"
        @on-click="onOptionClick"
      />
    </div>
    <GalleryOptionItem
      v-if="seeMoreOption"
      :option="seeMoreOption"
      :is-see-more="true"
      :rest-options-amount="restOptionsAmount + 1"
      class="hb-swatch-tile"
      @on-click="onOptionClick"
    />
    <select ref="hiddenOptions" class="hidden" :name="galleryOptionName">
      <option
        :value="activeOptionId || ''"
        :src="activeOption?.image_product_option_value"
        :data-title="activeOption?.name"
      >
        {{ activeOption?.name }}
      </option>
    </select>
  </div>

  <AdaptiveModal
    v-model:active="store.showOptionsModal"
    :modal-props="{
      size: '2xl',
      closeLabel: t('global.accessibility.close'),
      ariaLabel: t('product.options.color_picker'),
      alwaysOnTop: true
    }"
    :drawer-props="{
      closeLabel: t('global.accessibility.close'),
      ariaLabel: t('product.options.color_picker')
    }"
  >
    <GalleryOptionsList
      :active-option-id="activeOptionId"
      :active-option-title="activeOption?.title"
      @on-close="closeModal"
      @on-click="onModalOptionClick"
    />
  </AdaptiveModal>
</template>

<script setup>
import { useWindowSize } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';

import AdaptiveModal from '@/shared/components/UI/AdaptiveModal.vue';
import { useTranslations } from '@/shared/composables/useTranslations.ts';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';

import GalleryOptionItem from './GalleryOptionItem.vue';
import GalleryOptionsList from './GalleryOptionsList.vue';

const { width } = useWindowSize();
const { t } = useTranslations();
const optionsOnPage = ref([]);
const hiddenOptions = ref(null);

const store = useProductOptionsStore();

const activeOption = computed(() => store.activeOption);
const activeOptionId = computed(() => store.activeOption?.id);
const blocksNumber = computed(() => store.blocksNumberCalculation(width.value));
const galleryOptionName = computed(
  () => `option[${store.kind?.name.toLowerCase() || 'default'}]`
);

// stock
const optionsInStock = computed(() => store.optionsInStock);
const optionsOutOfStock = computed(() => store.optionsOutOfStock);
const totalOptions = computed(() => [
  ...optionsInStock.value,
  ...optionsOutOfStock.value
]);
const restOptionsAmount = computed(
  () => totalOptions.value.length - optionsOnPage.value.length
);
const seeMoreOption = computed(() =>
  restOptionsAmount.value > 0 ? optionsOnPage.value.at(-1) : null
);
const pickerOptions = computed(() =>
  seeMoreOption.value ? optionsOnPage.value.slice(0, -1) : optionsOnPage.value
);

// The last tile on the row becomes the see-more tile whenever options are
// hidden, so it is the last index that also carries the +N badge.
const isSeeMoreAt = (index) =>
  index + 1 == optionsOnPage.value.length && restOptionsAmount.value > 0;

const onOptionClick = ({ option, isSeeMore }) => {
  if (isSeeMore) {
    store.showOptionsModal = true;
  } else {
    handleVariantSelection(option);
  }
};

const onModalOptionClick = ({ option, closeModal: shouldClose = true }) => {
  if (shouldClose) {
    closeModal();
  }
  handleVariantSelection(option);
};

const handleVariantSelection = (variant) => {
  store.updateActiveOption(variant);
  updateOptionsOnPage();
};

const visibleBlocks = computed(() =>
  Math.min(blocksNumber.value, totalOptions.value.length)
);

const setOptionsPerPage = () => {
  if (optionsInStock.value.length >= visibleBlocks.value) {
    optionsOnPage.value = optionsInStock.value.slice(0, visibleBlocks.value);
  }
  if (optionsInStock.value.length < visibleBlocks.value) {
    const diff = visibleBlocks.value - optionsInStock.value.length;
    const oosOptions = optionsOutOfStock.value.slice(0, diff);
    optionsOnPage.value = [...optionsInStock.value, ...oosOptions];
  }
};

const isActiveOutside = computed(() => {
  if (!activeOptionId.value) {
    return false;
  }

  const activeOptionIndex = optionsOnPage.value.findIndex(
    (option) => option.id == activeOptionId.value
  );

  if (activeOptionIndex < 0) {
    return true;
  }
  if (isSeeMoreAt(activeOptionIndex)) {
    return true;
  }
  return false;
});

const updateOptionsOnPage = () => {
  if (!isActiveOutside.value) return;

  optionsOnPage.value = [
    activeOption.value,
    ...optionsOnPage.value.slice(0, -1)
  ];
};

onMounted(() => {
  optionsOnPage.value = totalOptions.value.slice(0, visibleBlocks.value);

  if (activeOption.value) {
    handleVariantSelection(activeOption.value);
  }
});

const closeModal = () => {
  store.showOptionsModal = false;
};

watch(width, () => {
  setOptionsPerPage();
  updateOptionsOnPage();
});
</script>
